import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { MinusSquareOutlined, DeleteOutlined } from '@ant-design/icons';
import { activePeriod, disabledDate } from '../../utils/date';
import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/expenseSlice';
import { CONVEYANCES_LIST_FIELDS } from '../../apollo/fragments';
import {
  EMPLOYEE_LOOKUP_QUERY,
  CONVEYANCE_TYPE_OPTIONS_QUERY,
} from '../../apollo/queries';
import {
  ADD_CONVEYANCE_MUTATION,
  UPDATE_CONVEYANCE_MUTATION,
  DELETE_CONVEYANCE_MUTATION,
} from '../../apollo/mutations';

import AddReviseConveyanceForm from '../../components/Expense/Forms/AddReviseConveyanceForm';
import ProfileCard from '../../components/Employee/ProfileCard';
import ConveyancesRevisions from './ConveyancesRevisions';
import useDeductionsQuery from './useDeductionsQuery';

const CONVEYANCES_QUERY = gql`
  ${CONVEYANCES_LIST_FIELDS}
  query Conveyances(
    $first: Int
    $page: Int
    $filters: [QueryGetConveyancesFilter]
    $sort: QueryGetConveyancesSorter
  ) {
    conveyances(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...ConveyanceListFields
      }
    }
  }
`;

const Conveyances = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.expense);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [showAddConveyanceModal, setShowAddConveyanceModal] = useState(false);
  const [showConveyanceReviseModal, setShowConveyanceReviseModal] = useState(
    false
  );
  const [activeConveyance, setActiveConveyance] = useState(null);
  const [activeConveyanceId, setActiveConveyanceId] = useState(null);
  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const { refetchDeductions } = useDeductionsQuery(activeConveyanceId);

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(CONVEYANCES_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const { loading: loadingTypes, data: types } = useQuery(
    CONVEYANCE_TYPE_OPTIONS_QUERY
  );

  const [addConveyance, { loading: adding }] = useMutation(
    ADD_CONVEYANCE_MUTATION
  );
  const [reviseConveyance, { loading: updating }] = useMutation(
    UPDATE_CONVEYANCE_MUTATION
  );
  const [deleteConveyance, { loading: deleting }] = useMutation(
    DELETE_CONVEYANCE_MUTATION
  );

  const onEmployeeLookup = (search) => {
    lookupEmployee({
      variables: {
        search,
        status: 'active',
      },
    });
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'EMP ID',
      dataIndex: ['employee', 'emp_id'],
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      render: (value, record) => {
        return <ProfileCard employee={record.employee} />;
      },
    },
    {
      title: 'Name',
      dataIndex: ['employee', 'first_name'],
      key: 'first_name',
      serverKey: 'NAME',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return (
          record && `${record.employee.first_name} ${record.employee.last_name}`
        );
      },
    },
    {
      title: 'Type',
      dataIndex: ['conveyance_type', 'name'],
      key: 'conveyance_type',
      serverKey: 'CONVEYANCE_TYPE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Effective From',
      dataIndex: 'effective_from',
      key: 'effective_from',
      serverKey: 'EFFECTIVE_FROM',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'month',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      serverKey: 'AMOUNT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      format: 'currency',
      className: 'cell-currency',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-150',
      render: (value, record) => {
        const for_month = moment(record.for_month);
        const isEditable = disabledDate(
          for_month,
          freezePrevMonth,
          currentMonth,
          prevMonth
        );
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<MinusSquareOutlined />}
              title="Revise"
              onClick={() => {
                onReviseConveyance(record);
              }}
            >
              Revise
            </Button>
            {ability.toDeleteConveyance && (
              <Popconfirm
                title="Are you sure to delete this revision?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const onConveyanceSubmit = (values, callBack) => {
    console.log('onConveyanceSubmit called with///', values);
    const variables = {
      id: values.employee.value,
      input: {
        conveyance_type_id: 1, // parseInt(values.conveyance_type_id, 10), // IMPORTANT: disabled for now
        effective_from: values.effective_from
          ? values.effective_from
              .startOf('month')
              .format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    addConveyance({
      variables,
    })
      .then(({ data }) => {
        const advance = data.addConveyance;
        console.log('onConveyanceSubmit finished', advance);
        if (advance) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddConveyanceModal(false);
        }
        // setActiveLeave(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = '';
        if (e.graphQLErrors) {
          e.graphQLErrors.forEach(({ extensions, message: errorMessage }) => {
            const { category, reason } = extensions;
            if (category === 'custom' && reason === 'Validation') {
              message.error(errorMessage);
            } else {
              error = errorMessage;
              message.error(
                error ? error : 'Some error occured, Please try again !!!'
              );
            }
          });
        }
      });
  };

  const onConveyanceReviseSubmit = (values, callBack) => {
    console.log('onConveyanceReviseSubmit called with///', values);
    const variables = {
      id: values.employee.value,
      input: {
        conveyance_type_id: 1,
        effective_from: values.effective_from
          ? values.effective_from
              .startOf('month')
              .format(Config.DATE_FORMAT_SERVER)
          : null,
        amount: values.amount ? values.amount : '',
        note: values.note ? values.note : '',
      },
    };
    reviseConveyance({
      variables,
    })
      .then(({ data }) => {
        const advance = data.reviseConveyance;
        console.log('onConveyanceReviseSubmit finished', advance);
        if (advance) {
          callBack({ status: true });
          onSuccessMutation();
          setShowConveyanceReviseModal(false);
        }
        // setActiveLeave(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
        callBack({ status: false });
      });
  };

  const onDeleteConfirm = (advance) => {
    console.log('onDeleteConfirm called with///', advance);
    deleteConveyance({
      variables: {
        id: advance.id,
      },
    })
      .then(({ data: { deleteConveyance } }) => {
        console.log('onDeleteConfirm finished', deleteConveyance);
        if (deleteConveyance.id) {
          onSuccessMutation('Conveyance deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onReviseConveyance = (deduction) => {
    setActiveConveyanceId(deduction.advance_id);
    setActiveConveyance(deduction);
    setShowConveyanceReviseModal(true);
  };

  const onCancelAddReviseConveyance = () => {
    setActiveConveyanceId(null);
    setActiveConveyance(null);
    setShowAddConveyanceModal(false);
    setShowConveyanceReviseModal(false);
  };

  return (
    <Spin spinning={loading}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.conveyances.data}
            columns={columns}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.conveyances.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={() => {
              console.log('Add new entry clicked!');
              setShowAddConveyanceModal(true);
            }}
            addNewEntryBtnLabel="Add Conveyance"
            expandable={{
              expandedRowClassName: (record, index, indent) => 'row-expanded',
              expandedRowRender: (record) => (
                <ConveyancesRevisions conveyance={record} />
              ),
              rowExpandable: (record) =>
                record.revisions && record.revisions.length > 1,
            }}
          />
        )}
      </Card>
      <Modal
        title={activeConveyance ? 'Edit Conveyance' : 'Add Conveyance'}
        visible={showAddConveyanceModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={() => onCancelAddReviseConveyance()}
        maskClosable={false}
      >
        <Spin spinning={adding}>
          <AddReviseConveyanceForm
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            // types={types ? types.conveyanceTypes : []}
            onLookup={onEmployeeLookup}
            onConveyanceSubmit={onConveyanceSubmit}
            onCancelClick={() => onCancelAddReviseConveyance()}
            fetching={fetching}
          />
        </Spin>
      </Modal>
      <Modal
        title="Revise Conveyance"
        visible={showConveyanceReviseModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={() => onCancelAddReviseConveyance()}
        maskClosable={false}
      >
        <Spin spinning={updating}>
          <AddReviseConveyanceForm
            conveyance={activeConveyance}
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            onLookup={onEmployeeLookup}
            onConveyanceSubmit={onConveyanceReviseSubmit}
            onCancelClick={() => onCancelAddReviseConveyance()}
            fetching={fetching}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Conveyances;
